import { t } from "i18next";
import { z } from "zod";
import { getAtcAfterAtsError, notificationSchema } from "../utils";
import {
  createAtcSchema,
  getAtcIncludesResourceEndTimesError,
} from "./sharedClosePortServiceSchemas";

const requiredPositiveIntegerField = (label: string) =>
  z.preprocess(
    (value) => {
      if (value === "" || value === null || value === undefined) {
        return undefined;
      }

      const parsed = Number(value);
      return Number.isNaN(parsed) ? value : parsed;
    },
    z
      .number({
        required_error: t("form-validation.required", { label }),
        invalid_type_error: t("form-validation.invalid-number", { label }),
      })
      .int(t("form-validation.invalid-number", { label }))
      .positive(t("form-validation.invalid-number", { label })),
  );

const optionalPositiveIntegerField = (label: string) =>
  z.preprocess(
    (value) => {
      if (value === "" || value === null || value === undefined) {
        return undefined;
      }

      const parsed = Number(value);
      return Number.isNaN(parsed) ? value : parsed;
    },
    z
      .number({
        invalid_type_error: t("form-validation.invalid-number", { label }),
      })
      .int(t("form-validation.invalid-number", { label }))
      .positive(t("form-validation.invalid-number", { label }))
      .optional(),
  );

const createResourceCountField = () => {
  const numberOfResourcesLabel = t(
    "vessel-call.movement-services.close-form.fields.number-of-resources",
  );

  return z.preprocess(
    (value) => {
      if (value === "" || value === null || value === undefined) {
        return undefined;
      }

      return typeof value === "string" ? value.trim() : String(value);
    },
    z
      .string({
        required_error: t("form-validation.required", { label: numberOfResourcesLabel }),
        invalid_type_error: t("form-validation.invalid-number", {
          label: numberOfResourcesLabel,
        }),
      })
      .superRefine((value, ctx) => {
        if (!value) {
          return;
        }

        if (value.includes(".") || value.includes(",")) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `${numberOfResourcesLabel} must be an integer (no decimals).`,
          });
          return;
        }

        const parsed = Number(value);
        if (!Number.isInteger(parsed)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `${numberOfResourcesLabel} must be an integer.`,
          });
          return;
        }

        if (parsed < 1) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `${numberOfResourcesLabel} must be a positive number.`,
          });
          return;
        }

        if (value.length > 4) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t("form-validation.must-have-maximum-digits", {
              label: numberOfResourcesLabel,
              max: 4,
            }),
          });
        }
      })
      .transform((value) => Number(value)),
  );
};

const usageTimeField = (label: string) =>
  z.preprocess(
    (value) => {
      if (value === "" || value === null || value === undefined) {
        return undefined;
      }

      const parsed = new Date(value as string | number | Date);
      return Number.isNaN(parsed.getTime()) ? value : parsed;
    },
    z.date({
      required_error: t("form-validation.required", { label }),
      invalid_type_error: t("form-validation.invalid-date", { label }),
    }),
  );

const createResourceSchema = () => {
  const resourceTypeLabel = t("vessel-call.movement-services.close-form.fields.resource-type");
  const startTimeLabel = t("vessel-call.movement-services.close-form.fields.start-time");
  const endTimeLabel = t("vessel-call.movement-services.close-form.fields.end-time");

  return z
    .object({
      resourceTypeCode: z
        .string()
        .min(1, t("form-validation.required", { label: resourceTypeLabel })),
      resourceTypeName: z.string().optional(),
      numberOfResources: createResourceCountField(),
      startTimeOfUsage: usageTimeField(startTimeLabel),
      endTimeOfUsage: usageTimeField(endTimeLabel),
    })
    .superRefine((value, ctx) => {
      if (value.endTimeOfUsage <= value.startTimeOfUsage) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["endTimeOfUsage"],
          message: t("form-validation.end-time-after-start"),
        });
      }
    });
};

type CloseShipChandlingRefineValue = {
  atc?: Date;
  resourcesAndEquipment?: Array<{
    startTimeOfUsage: Date;
    endTimeOfUsage: Date;
  }>;
};

export const closeShipChandlingSchema = (ats?: string | Date | null) =>
  createAtcSchema()
    .merge(
      z.object({
        numberOfPackagesHandled: requiredPositiveIntegerField(
          t("vessel-call.port-services.fields.number-of-packages-handled"),
        ),
        spares: optionalPositiveIntegerField("Spares"),
        incidents: z.string().max(2500).optional(),
        resourcesAndEquipment: z.array(createResourceSchema()).optional(),
      }),
    )
    .merge(notificationSchema)
    .superRefine((value, ctx) => {
      const parsedValue = value as CloseShipChandlingRefineValue;
      const atcError = getAtcAfterAtsError(parsedValue.atc, ats);

      if (atcError) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["atc"],
          message: atcError,
        });
      }

      if (!ats) {
        return;
      }

      const parsedAts = new Date(ats);
      if (Number.isNaN(parsedAts.getTime())) {
        return;
      }

      parsedValue.resourcesAndEquipment?.forEach((resource, index) => {
        if (resource.startTimeOfUsage <= parsedAts) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["resourcesAndEquipment", index, "startTimeOfUsage"],
            message: t("form-validation.start-time-after-ats"),
          });
        }
      });

      const atcResourceCoverageError = getAtcIncludesResourceEndTimesError(
        parsedValue.atc,
        parsedValue.resourcesAndEquipment?.map((resource) => resource.endTimeOfUsage) ?? [],
      );

      if (atcResourceCoverageError) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["atc"],
          message: atcResourceCoverageError,
        });
      }
    });

export type CloseShipChandlingSchema = z.infer<ReturnType<typeof closeShipChandlingSchema>>;
